<script lang="ts">
	import { Background } from "@components/Background.js";
	import BookMeeting from "@components/BookMeeting.svelte";
	import FaqItems from "@components/FaqItems.svelte";
	import FoldingCards from "@components/FoldingCards.svelte";
	import InfoCard from "@components/InfoCard.svelte";
	import Ol from "@components/Ol.svelte";
	import { OlSize } from "@components/OlProps.js";
	import ReferenceContents from "@components/ReferenceContents.svelte";
	import Section from "@components/Section.svelte";
	import ServicesCards from "@components/ServicesCards.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import Icon from "@components/common/Icon.svelte";
	import Link from "@components/common/Link.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import Layout from "@components/layout/Layout.svelte";
	import arrowNext from "@core/assets/icons/arrow-next.svg?raw";
	import { ReferencePlacement } from "@core/schema/ReferencePlacement.js";
	import { getClientsPath } from "@core/schema/paths/getClientsPath.js";
	import { getConnectWithFastestPath } from "@core/schema/paths/getConnectWithFastestPath.js";
	import { getReferencePath } from "@core/schema/paths/getReferencePath.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { BookMeetingPerson, getBookMeetingPeople } from "../data/getBookMeetingPeople.js";
	import { getFaqsHireTeam } from "../data/getFaqsHireTeam.js";
	import { getHireStepByStep } from "../data/getHireStepByStep.js";
	import { getReasonsForCoop } from "../data/getReasonsForCoop.js";
	import { getServicesHire } from "../data/getServicesHire.js";
	import { getServicesToHire } from "../data/getServicesToHire.js";
	import { getWhenHire } from "../data/getWhenHire.js";
	import type { HireItTeamProps } from "./HireItTeamProps.js";

	const { tenant } = getGlobalTemplateProps();
	const _: HireItTeamProps = $props();
</script>

<Layout withStructuredData>
	<Section background={Background.LightGradient} centered gridHeadline="container-6" gridDesc="container-6" hero>
		{#snippet headline()}
			<Formatted text="Pronajmeme vám zkušený cg[[IT tým]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted text="Využijte pomoci jednoho specialisty, nebo celého týmu profíků." />
			</p>
		{/snippet}
		{#snippet cta()}
			<Link
				variant={ButtonVariant.PhloxOutlineLightBg}
				class="flex items-center justify-center mx-auto mt-2"
				href={getConnectWithFastestPath(tenant)}
			>
				<Formatted text="Zeptat se na podrobnosti" />
				<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
			</Link>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
		paddingFollowNext
	>
		{#snippet headline()}
			<Formatted text="Zakládáme si na férovosti, výsledcích a táhneme za cg[[jeden provaz]]" />
		{/snippet}
		{#snippet desc()}
			<p>
				<Formatted
					text="Vývojář, analytik, projektový manažer nebo třeba tester softwaru? Žádný problém –  půjčíme vám odborníky s těmito i dalšími dovednostmi."
				/>
			</p>
			<p>
				<Formatted
					text="sp[[Náš vývojový tým se specializuje na javascriptové technologie]] (zejména Node.js, Svelte, Angular) a vývoj na cloudu (primárně Amazon Web Services). "
				/>
			</p>
		{/snippet}
	</Section>

	<Section
		background={Background.LightGradient}
		paddingFollowNext
		gridHeadline="container-8 lg:text-center"
		gridDesc="container-8"
		centered
	>
		{#snippet headline()}
			<!-- <Formatted text="Pomocí kterých řešení firmy cg[[digitalizujeme a automatizujeme]]?" /> TODO: Overflows -->
			<Formatted text="Pomocí kterých řešení firmy cg[[digitalizujeme?]]" />
		{/snippet}
	</Section>
	<Section gridChildren="max-md:full-width" background={Background.White} noPadding="both">
		<div
			class="grid grid-cols-1 md:grid-cols-3 gap-1 max-lg:full-width lg:px-1 bg-fstlight-100 border-t-4 border-fstlight-100"
		>
			{#each getServicesHire(tenant) as { title, desc }, index (index)}
				<InfoCard {title} {desc} indexNumber={index + 1} />
			{/each}
		</div>
	</Section>
	<Link
		variant={ButtonVariant.Phlox}
		class="flex items-center justify-center mx-auto mt-8 lg:mt-12"
		href={getConnectWithFastestPath(tenant)}
	>
		<Formatted text="Zeptat se na podrobnosti" />
		<Icon icon={arrowNext} class="ml-2.5 text-fstgrey-900 w-5"></Icon>
	</Link>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="S čím vám dokážeme cg[[pomoci]]?" />
		{/snippet}
		{#snippet desc()}
			<ul class="custom-list">
				{#each getWhenHire(tenant) as { desc }, index (index)}
					<li>{desc}</li>
				{/each}
			</ul>
		{/snippet}
	</Section>

	<Section
		gridHeadline="container-12 md:container-8 xl:container-start-2 xl:container-end-5"
		gridDesc="container-12 md:container-8 xl:container-start-7 xl:container-end-10"
	>
		{#snippet headline()}
			<Formatted text="Jak pronájem IT týmu cg[[probíhá]]" />
		{/snippet}
		{#snippet desc()}
			<div class="flex flex-col gap-8">
				<Ol items={getHireStepByStep(tenant)} size={OlSize.Medium} />
			</div>
		{/snippet}
		<Link
			variant={ButtonVariant.Phlox}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getConnectWithFastestPath(tenant)}
			><Formatted text="Rezervovat online schůzku" />
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>

	<Section id="reference" gridHeadline="container-8" centered>
		{#snippet headline()}
			<Formatted text="Komu jsme digitalizací a automatizací zrychlili růst podnikání?" />
		{/snippet}
		<div class="mt-12">
			<ReferenceContents limit={8} placement={ReferencePlacement.ItRental} />
		</div>
		<Link
			variant={ButtonVariant.GreenOutlineLightBg}
			class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
			href={getReferencePath(tenant)}
			>Zobrazit více
			<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
		</Link>
	</Section>
	<Section centered gridHeadline="container-6" noPadding="bot">
		{#snippet headline()}
			<Formatted text="S čím dalším vám cg[[pomůžeme]]" />
		{/snippet}
	</Section>
	<ServicesCards cards={getServicesToHire(tenant)} />

	<Section centered gridHeadline="container-6">
		{#snippet headline()}
			<Formatted text="Časté otázky k pronájmu cg[[IT týmu]]" />
		{/snippet}
		<FaqItems faqs={getFaqsHireTeam(tenant)} />
	</Section>

	<Section
		background={Background.Onyx}
		gridHeadline="container-6 lg:container-start-1 lg:container-end-5 xl:container-start-2 xl:container-end-4 max-lg:text-center"
		gridDesc="lg:container-start-7 lg:container-end-12 xl:container-start-6 xl:container-end-11"
	>
		{#snippet headline()}
			<Formatted text="cg[[Proč]] si plácnout s cg[[Fastest Solution]]" />
		{/snippet}
		{#snippet desc()}
			<FoldingCards color={Background.Onyx} cards={getReasonsForCoop(tenant)} />
		{/snippet}
		<div class="container-12">
			<Link
				variant={ButtonVariant.Phlox}
				class="mx-auto flex items-center justify-center mt-8 lg:mt-12"
				href={getConnectWithFastestPath(tenant)}
				>Chci fastest služby
				<Icon icon={arrowNext} class="ml-2 text-fstgrey-900"></Icon>
			</Link>
		</div>
	</Section>

	{@const contacts = getBookMeetingPeople(tenant)}
	<BookMeeting contacts={[contacts[BookMeetingPerson.Kucera], contacts[BookMeetingPerson.Svejda]]} />
</Layout>
